import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import type { Database } from './schema'

/**
 * Supabase client singleton.
 *
 * Lazy-constructed so the bundle still works on builds where the env vars
 * weren't injected (offline local-first mode keeps functioning). The
 * client is only ever needed if and when a user signs in — anonymous
 * local-mode never touches it.
 *
 * Env vars are read from `import.meta.env` (Vite). Both must be present
 * for the client to be usable; if either is missing we return `null` and
 * the rest of the app falls back to local-only behaviour with a console
 * warning. This is intentional — it lets contributors run `pnpm dev`
 * without a Supabase project and still exercise the local-first path.
 */

type CloudClient = SupabaseClient<Database>

let _client: CloudClient | null = null

/** True if both env vars are populated and a client can be constructed. */
export function isSupabaseConfigured(): boolean {
  return Boolean(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY)
}

/**
 * Returns the singleton client, or `null` if Supabase isn't configured.
 * Cloud-mode callers should check for `null` and degrade gracefully.
 */
export function getSupabaseClient(): CloudClient | null {
  if (_client) return _client
  if (!isSupabaseConfigured()) {
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      // Loud in dev so contributors notice missing config; silent in prod
      // since the offline-local fallback is the intended UX there.
      console.info(
        '[debtos] Supabase env vars not set. Running in local-only mode. ' +
          'Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to .env.local to enable cloud sync.'
      )
    }
    return null
  }

  _client = createClient<Database>(
    import.meta.env.VITE_SUPABASE_URL as string,
    import.meta.env.VITE_SUPABASE_ANON_KEY as string,
    {
      auth: {
        // Keep the session in localStorage so a phone refresh doesn't sign
        // the user out. Supabase JS handles refresh-token rotation
        // automatically once `autoRefreshToken` is on.
        persistSession: true,
        autoRefreshToken: true,
        // The magic-link callback page parses the redirect URL on landing;
        // detectSessionInUrl lets the client pick up the session
        // automatically rather than us re-parsing the hash.
        detectSessionInUrl: true,
      },
    }
  )
  return _client
}

/**
 * Variant that throws when the client isn't available — useful inside
 * code paths that should only run for signed-in users (e.g. the cloud
 * repos). Catches misuse early.
 */
export function requireSupabaseClient(): CloudClient {
  const client = getSupabaseClient()
  if (!client) {
    throw new Error(
      '[debtos] Supabase client is not configured. ' +
        'This code path should only run for signed-in users with VITE_SUPABASE_* set.'
    )
  }
  return client
}

/** Reset hook for tests. Not used at runtime. */
export function __resetSupabaseClientForTests(): void {
  _client = null
}
